// src/utils/ScheduleUtils.ts
import { Message } from "../layouts/AskDatatram/ChatInterface";
import { sendCampaignEmail} from "src/services/CampaignAPI";
import i18n from "src/i18n/config";
import { getApiLangFromI18n } from "src/utils/apiLang";

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(`chatScreen.${key}`, options);

export const getTime = (): string =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const getSchedulePresets = () => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const nextMonday = new Date(today);
  const dayOffset = (8 - today.getDay()) % 7 || 7;
  nextMonday.setDate(today.getDate() + dayOffset);

  const formatDay = (d: Date) =>
    d.toLocaleDateString("en-US", { weekday: "long" });

  return [
    { label: `Tomorrow Morning (9:00 AM - ${formatDay(tomorrow)})`, time: "09:00", offset: 1 },
    { label: `Tomorrow Evening (6:00 PM - ${formatDay(tomorrow)})`, time: "18:00", offset: 1 },
    { label: `Next Monday (9:00 AM - ${formatDay(nextMonday)})`, time: "09:00", offset: dayOffset },
  ];
};

export const handleQuickSchedule = (
  preset: any,
  setSelectedPreset: (preset: any) => void,
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>
) => {
  setSelectedPreset(preset);
  setQuery(prev => [
    ...prev,
    {
      sender: "bot",
      text: t("presetScheduleConfirm", { label: preset.label }),
      timestamp: getTime(),
      type: "confirmation",
      preset,
    },
  ]);
};

/** ✅ Only finalize once user confirms */
export const handleScheduleConfirmation = async (
  response: "yes" | "no",
  preset: any,
  setScheduledDate: (date: string | null) => void,
  setScheduledTime: (time: string | null) => void,
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>,
  campaignName?: string,
  audienceList?: string[],
  htmlContent?: string
) => {
  if (response === "yes") {
    const selectedDate = new Date();
    if (preset.dayOffset)
      {selectedDate.setDate(selectedDate.getDate() + preset.dayOffset);}
    else {selectedDate.setDate(selectedDate.getDate() + 1);}

    const dateStr = selectedDate.toISOString().split("T")[0];
    setScheduledDate(dateStr);
    setScheduledTime(preset.time);

    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("schedulingCampaignFor", { name: campaignName, when: preset.label }),
        timestamp: getTime(),
        type: "botQuestion",
      },
    ]);

    try {
      const formattedDateTime = `${dateStr}T${preset.time}:00Z`;
      const payload = {
        campaign_name: campaignName || "Untitled Campaign",
        send_to: audienceList || ["default@example.com"],
        html: htmlContent || "<p>No HTML Content</p>",
        send_at: formattedDateTime,
        company_name_engagements: localStorage.getItem("company_name") || "Unknown",
        lang: getApiLangFromI18n(i18n),
      };

      await sendCampaignEmail(payload);

      setQuery(prev => [
        ...prev,
        {
          sender: "bot",
          text: t("campaignScheduledSuccess", { name: payload.campaign_name, when: preset.label }),
          timestamp: getTime(),
          type: "botQuestion",
        },
      ]);
    } catch (_error) {
      setQuery(prev => [
        ...prev,
        {
          sender: "bot",
          text: t("failedToScheduleCampaign"),
          timestamp: getTime(),
          type: "botQuestion",
        },
      ]);
    }
  } else {
    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("noWorriesPickAnotherTime"),
        timestamp: getTime(),
        type: "schedulePicker",
      },
    ]);
  }
};

/** ✅ Handle manual confirm (when user selects date/time manually) */
export const handleConfirmSchedule = async (
  scheduledDate: string | null,
  scheduledTime: string | null,
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>,
  campaignName?: string,
  audienceList?: string[],
  htmlContent?: string
) => {
  if (!scheduledDate || !scheduledTime) {
    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("selectDateAndTimeWarning"),
        timestamp: getTime(),
        type: "botQuestion",
      },
    ]);
    return;
  }

  const formatted = `${scheduledDate}T${scheduledTime}:00Z`;
  const when = `${scheduledDate} ${scheduledTime}`;

  // Step 1: Inform the user immediately
  setQuery(prev => [
    ...prev,
    {
      sender: "bot",
      text: t("schedulingCampaignFor", { name: campaignName, when }),
      timestamp: getTime(),
      type: "botQuestion",
    },
  ]);

  try {
    // Step 2: Call API to schedule campaign
    const payload = {
      campaign_name: campaignName || "Untitled Campaign",
      send_to: audienceList || ["default@example.com"],
      html: htmlContent || "<p>No HTML Content</p>",
      send_at: formatted,
      company_name_engagements: localStorage.getItem("company_name") || "Unknown",
      lang: getApiLangFromI18n(i18n),
    };

    const res = await sendCampaignEmail(payload);

    // Step 3: Notify success
    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("campaignScheduledSuccess", { name: payload.campaign_name, when }),
        timestamp: getTime(),
        type: "botQuestion",
      },
    ]);
  } catch (_error) {
    // Step 4: Handle failure
    setQuery(prev => [
      ...prev,
      {
        sender: "bot",
        text: t("failedToScheduleCampaign"),
        timestamp: getTime(),
        type: "botQuestion",
      },
    ]);
  }
};

/** Cancel scheduling */
export const handleCancelSchedule = (
  setScheduledDate: (date: string | null) => void,
  setScheduledTime: (time: string | null) => void,
  setQuery: React.Dispatch<React.SetStateAction<Message[]>>
) => {
  setScheduledDate(null);
  setScheduledTime(null);

  setQuery(prev => [
    ...prev,
    {
      sender: "bot",
      text: t("schedulingCanceled"),
      timestamp: getTime(),
      type: "botQuestion",
    },
  ]);
};
